import { HttpMethods, makeRequest } from '../helpers/http.helper';
import { OrganizationType } from '../types/organization.type';
import type { DashboardByBranchResponse, PilotDashboardStatistics } from '../types/dashboard.type';
import type { CollectionSiteSchema } from '../types/collection-site.type';
import { getEsgDashboardByBranch } from './esg-dashboard.service';
import { getCollectionSites } from './collection-site.service';
import {
  ALL_TIME_MONTH_VALUE,
  getCurrentDashboardMonthValue,
  monthValueToYearMonth,
} from '../utils/dashboardMonth.helpers';
import { mapEsgByBranchResponse } from '../utils/esgByBranch.helpers';

/**
 * Charity orgs use the waste-outlet dashboard API (same operational shape;
 * financial metrics are filtered out in the Zero catalog).
 */
const DASHBOARD_PATH_BY_TYPE: Record<OrganizationType, string> = {
  [OrganizationType.WASTE_OUTLET]: '/dashboard/waste-outlet',
  [OrganizationType.CHARITY]: '/dashboard/waste-outlet',
  [OrganizationType.RECYCLER]: '/dashboard/recycler',
  [OrganizationType.TRUCK]: '/dashboard/trucker',
};

export const getDashboardStatistics = (
  orgId: string,
  orgType: OrganizationType,
  monthDate?: string
) => {
  const base = DASHBOARD_PATH_BY_TYPE[orgType];
  return makeRequest({
    url: `${base}/${orgId}`,
    method: HttpMethods.GET,
    params: monthDate ? { month_date: monthDate } : undefined,
  });
};

/** Pilot-safe dashboard projection. The API returns only these four metrics. */
export const getPilotDashboardStatistics = (
  orgId: string,
  monthDate: string,
): Promise<PilotDashboardStatistics> =>
  makeRequest({
    url: `/dashboard/pilot/${orgId}`,
    method: HttpMethods.GET,
    params: { month_date: monthDate },
  }) as Promise<PilotDashboardStatistics>;

/** Lifetime dashboard (no month_date). Paths match monthly dashboard base + `/overview`. */
export const getDashboardOverview = (
  orgId: string,
  orgType: OrganizationType
) => {
  const base = DASHBOARD_PATH_BY_TYPE[orgType];
  return makeRequest({
    url: `${base}/${orgId}/overview`,
    method: HttpMethods.GET,
  });
};

async function fetchCollectionSitesForOrg(
  orgId: string,
): Promise<CollectionSiteSchema[]> {
  try {
    const response = await getCollectionSites({ organization_id: orgId });
    const data = response as { result?: CollectionSiteSchema[] };
    return Array.isArray(data.result) ? data.result : [];
  } catch {
    return [];
  }
}

/**
 * Branch analysis for waste outlets — uses ESG by-branch (top-level JSON, not wrapped).
 * @see docs/collection-site-branch-analysis.md
 */
export const getDashboardByBranch = async (
  orgId: string,
  monthDate?: string
): Promise<DashboardByBranchResponse> => {
  const month = monthDate || getCurrentDashboardMonthValue();
  const isAllTime = month === ALL_TIME_MONTH_VALUE;

  const [esgResponse, collectionSites] = await Promise.all([
    isAllTime
      ? getEsgDashboardByBranch("waste-outlet", orgId, { all_time: true })
      : getEsgDashboardByBranch(
          "waste-outlet",
          orgId,
          monthValueToYearMonth(month),
        ),
    fetchCollectionSitesForOrg(orgId),
  ]);

  return mapEsgByBranchResponse(esgResponse, month, collectionSites);
};
